/* eslint-disable react/jsx-pascal-case */
/**
 * icons.tsx — shared SVG components for UnifiedCardDetailModal.
 *
 * Inline because the design HTML uses very specific 2.2–2.4 stroke widths
 * at small (10–14px) sizes; MUI's stock icons render too thick at those
 * sizes for the modal's density. Keeping these inline (instead of using
 * symbol/use refs) means the icons participate in `color: currentColor`
 * cleanly and don't pull in an external SVG sprite.
 */
import React from 'react';

const sw = (n: number): { strokeWidth: number } => ({ strokeWidth: n });

export const I = {
  Back: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Edit: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  ChevronDown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Filter: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3z" />
    </svg>
  ),
  Search: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  ),
  Plus: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(3)} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Money: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Doors: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
    </svg>
  ),
  Source: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
    </svg>
  ),
  Tech: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  Link: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Cal: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Thought: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Sms: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  Email: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  ),
  // Phase 7c c1: Doc / Form icon — used by `task_type='form'` rows on
  // the live task row (Sandbox-Native port). Slate-tinted, non-glow
  // treatment signals "open a form, not a comm action."
  Doc: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),

  Todo: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4" {...sw(2.4)} />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  People: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
  Thread: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Attach: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  Zap: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Pin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v3l-3 3v3h12v-3l-3-3V5a3 3 0 0 0-3-3zM12 14v8" />
    </svg>
  ),
  ArrowUpRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.8)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17 17 7M7 7h10v10" />
    </svg>
  ),
  ArrowDownLeft: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.8)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 7 7 17M17 17H7V7" />
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Expand: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6M14 10l7-7M9 21H3v-6M10 14l-7 7" />
    </svg>
  ),
  Open: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.4)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </svg>
  ),
  Info: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" {...sw(2.4)} />
    </svg>
  ),
  Workflow: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="15" y="15" width="6" height="6" rx="1" />
      <path d="M9 6h6a3 3 0 0 1 3 3v6" />
    </svg>
  ),
  // CD-20 typed todos: proposal (doc + check) + AI (sparkle — NOT the ⚡ Zap,
  // which stays exclusive to the automation indicator).
  Proposal: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M9 15l2 2 4-4" />
    </svg>
  ),
  Ai: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
      <path d="M19 15l.6 1.9L21.5 17.5l-1.9.6L19 20l-.6-1.9L16.5 17.5l1.9-.6L19 15z" />
    </svg>
  ),
  // Primary-contact toggle (contact card). Filled = primary; outline =
  // "set as primary". Same hand-rolled star polygon both ways so the glyph
  // stays pixel-aligned when it swaps fill on click.
  Star: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  StarBorder: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...sw(2.2)} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
};
